import { apiFetch } from "./http";

export type AdminUser = {
  id: number;
  email: string;
  full_name: string;
  role: string;
  org_id?: number | null;
};

export type LoginResponse = { token: string; user: AdminUser };

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await apiFetch("/api/v1/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim(), password, scope: "admin" }),
  });
  if (res.status === 401) throw new Error("E-posta veya parola hatalı.");
  if (!res.ok) throw new Error(`Giriş başarısız (HTTP ${res.status}).`);
  return res.json();
}

export type Customer = {
  id: number;
  name: string;
  type: "bireysel" | "kurumsal";
  email?: string;
  status: "active" | "passive";
  credit_balance: number; // doküman üretim kredisi (1 kredi = 1 immutable GSPR dokümanı)
  // gspr_count — bu müşteri için ÜRETİLMİŞ GSPR dokümanı sayısı (çeviriler
  // sayılmaz). Kredi bakiyesinden ayrı bilgi: bakiye "hakkı ne kadar kaldı",
  // bu "ne kadar kullandı" der. Yalnız liste ucunda dolu gelir.
  gspr_count: number;
  created_at: string;
};

export async function listCustomers(): Promise<Customer[]> {
  const res = await apiFetch("/api/v1/admin/customers");
  if (!res.ok) throw new Error(`Müşteriler alınamadı (HTTP ${res.status}).`);
  const data = (await res.json()) as { customers: Customer[] | null };
  return data.customers ?? [];
}

// Admin'de tekil müşteri endpoint'i yok; listeden bulur (liste küçük).
export async function getCustomer(id: number): Promise<Customer | null> {
  const list = await listCustomers();
  return list.find((c) => c.id === id) ?? null;
}

export type CustomerInput = {
  name: string;
  type: "bireysel" | "kurumsal";
  email?: string;
  status?: "active" | "passive";
  // Müşteri panelinе girecek giriş hesabı (oluşturmada zorunlu).
  login_email?: string;
  login_password?: string;
  login_full_name?: string;
};

// grantCredits — müşteriye manuel kredi tanımla (admin_grant). credits sıfırdan farklı.
export async function grantCredits(id: number, credits: number): Promise<{ credit_balance: number }> {
  const res = await apiFetch(`/api/v1/admin/customers/${id}/credits`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credits }),
  });
  if (!res.ok) throw new Error(await errText(res, "Kredi tanımlanamadı"));
  return res.json();
}

// createCustomer artık müşteri + giriş kullanıcısını birlikte döner.
export async function createCustomer(
  input: CustomerInput,
): Promise<{ customer: Customer; user: Staff }> {
  const res = await apiFetch("/api/v1/admin/customers", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await errText(res, "Müşteri oluşturulamadı"));
  return res.json();
}

export async function updateCustomer(id: number, input: Partial<CustomerInput>): Promise<Customer> {
  const res = await apiFetch(`/api/v1/admin/customers/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await errText(res, "Müşteri güncellenemedi"));
  return res.json();
}

export type Staff = {
  id: number;
  email: string;
  full_name: string;
  role: string;
  org_id?: number | null;
  is_active: boolean;
  created_at: string;
  /** Self-servis kaydın yapıldığı IP. Boş/undefined = bilinmiyor: kolon
   *  eklenmeden (2026-08-17) önce açılmış hesap ya da admin'in oluşturduğu
   *  kullanıcı. Yalnız ADMIN ucunda döner — müşteri panelinin kendi personel
   *  listesine bilinçli olarak girmez. */
  register_ip?: string;
  /** Kaydın yapıldığı tarayıcının ham User-Agent'ı. IP ile birlikte anlamlı:
   *  ortak çıkış (NAT) arkasındaki iki kaydı tek başına IP ayırt edemez.
   *  Aynı görünürlük kuralı — yalnız admin ucunda döner. */
  register_user_agent?: string;
};

export async function listStaff(customerID: number): Promise<Staff[]> {
  const res = await apiFetch(`/api/v1/admin/customers/${customerID}/staff`);
  if (!res.ok) throw new Error(`Personel alınamadı (HTTP ${res.status}).`);
  const data = (await res.json()) as { staff: Staff[] | null };
  return data.staff ?? [];
}

export async function createStaff(
  customerID: number,
  input: { email: string; password: string; full_name: string },
): Promise<Staff> {
  const res = await apiFetch(`/api/v1/admin/customers/${customerID}/staff`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await errText(res, "Personel oluşturulamadı"));
  return res.json();
}

export async function setStaffActive(customerID: number, staffID: number, active: boolean): Promise<Staff> {
  const res = await apiFetch(`/api/v1/admin/customers/${customerID}/staff/${staffID}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ is_active: active }),
  });
  if (!res.ok) throw new Error(await errText(res, "Personel güncellenemedi"));
  return res.json();
}

// --- Fatura adresleri + Projeler (şimdilik salt-okunur; müşteri panelinden oluşturulacak) ---

export type BillingAddress = {
  id: number;
  org_id: number;
  title: string;
  address_type: "bireysel" | "kurumsal";
  first_name?: string;
  last_name?: string;
  tc_kimlik_no?: string;
  company_name?: string;
  tax_office?: string;
  tax_no?: string;
  country: string;
  state?: string;
  city?: string;
  postal_code?: string;
  email?: string;
  phone?: string;
  full_address?: string;
  created_at: string;
};

export type Project = {
  id: number;
  org_id: number;
  name: string;
  description?: string;
  billing_address_id?: number | null;
  billing_title?: string;
  status: "taslak" | "aktif" | "tamamlandi";
  created_at: string;
};

export async function listBillingAddresses(customerID: number): Promise<BillingAddress[]> {
  const res = await apiFetch(`/api/v1/admin/customers/${customerID}/billing-addresses`);
  if (!res.ok) throw new Error(`Fatura adresleri alınamadı (HTTP ${res.status}).`);
  const data = (await res.json()) as { billing_addresses: BillingAddress[] | null };
  return data.billing_addresses ?? [];
}

export async function listProjects(customerID: number): Promise<Project[]> {
  const res = await apiFetch(`/api/v1/admin/customers/${customerID}/projects`);
  if (!res.ok) throw new Error(`Projeler alınamadı (HTTP ${res.status}).`);
  const data = (await res.json()) as { projects: Project[] | null };
  return data.projects ?? [];
}

// --- Müşteri geçmişi (salt-okunur) ---------------------------------------
// Müşteri panelindeki Dashboard'un admin karşılığı. Backend:
// go/internal/api/handler/admin_history.go

export type CustomerOverview = {
  org_id: number;
  org_name: string;
  credit_balance: number;
  documents: number;
  staff: number;
  projects: number;
  gspr_sessions: number;
  analyses: number;
  chat_sessions: number;
  doc_analyses: number;
};

export type GeneratedDoc = {
  id: number;
  org_id: number;
  session_id?: string;
  product_name: string;
  risk_class?: string;
  document_code?: string;
  version?: string;
  status?: "preparing" | "ready";
  source_doc_id?: number; // dolu ise bu satır bir ÇEVİRİ
  language?: string;
  created_at: string;
  // Tam snapshot yalnız tekil çekimde gelir (getCustomerDocument)
  profile?: unknown;
  spr_map?: unknown;
  classification?: unknown;
  summary?: unknown;
  gap_rows?: unknown;
};

export type GSPRSession = {
  id: string;
  org_id?: number;
  profile?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type Payment = {
  id: number;
  reference: string;
  credits: number;
  amount_usd: number;
  amount_tl: number;
  usd_try_rate: number;
  usd_eur_rate: number;
  status: "pending" | "paid" | "failed";
  relay_order_no?: string;
  hostrefnum?: string;
  message?: string;
  created_at: string;
  paid_at?: string;
};

export type PaymentsResult = {
  payments: Payment[];
  paid_usd: number;
  paid_tl: number;
  paid_credits: number;
};

export type CreditEntry = {
  id: number;
  delta: number;
  reason: "purchase" | "document_generated" | "admin_grant" | "refund";
  ref_payment_id?: number;
  ref_document_id?: number;
  balance_after: number;
  created_at: string;
};

export type AnalysisEntry = {
  id: number;
  user_id: number;
  user_name?: string;
  user_email?: string;
  question: string;
  answer?: string;
  answer_preview?: string;
  citations?: unknown;
  model?: string;
  chunks_used?: number;
  usage_ms?: number;
  created_at: string;
};

export type ChatSessionRow = {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
};

async function getJSON<T>(url: string, what: string): Promise<T> {
  const res = await apiFetch(url);
  if (!res.ok) throw new Error(`${what} alınamadı (HTTP ${res.status}).`);
  return (await res.json()) as T;
}

export async function getCustomerOverview(id: number): Promise<CustomerOverview> {
  return getJSON<CustomerOverview>(`/api/v1/admin/customers/${id}/overview`, "Özet");
}

export async function listCustomerDocuments(id: number): Promise<GeneratedDoc[]> {
  const d = await getJSON<{ documents: GeneratedDoc[] | null }>(
    `/api/v1/admin/customers/${id}/documents`, "Dokümanlar");
  return d.documents ?? [];
}

export async function getCustomerDocument(id: number, docID: number): Promise<GeneratedDoc> {
  return getJSON<GeneratedDoc>(`/api/v1/admin/customers/${id}/documents/${docID}`, "Doküman");
}

export async function listCustomerGSPRSessions(id: number): Promise<GSPRSession[]> {
  const d = await getJSON<{ sessions: GSPRSession[] | null }>(
    `/api/v1/admin/customers/${id}/gspr-sessions`, "GSPR oturumları");
  return d.sessions ?? [];
}

export async function listCustomerPayments(id: number): Promise<PaymentsResult> {
  const d = await getJSON<{ payments: Payment[] | null } & Omit<PaymentsResult, "payments">>(
    `/api/v1/admin/customers/${id}/payments`, "Ödemeler");
  return { ...d, payments: d.payments ?? [] };
}

export async function listCustomerCreditLedger(id: number): Promise<CreditEntry[]> {
  const d = await getJSON<{ entries: CreditEntry[] | null }>(
    `/api/v1/admin/customers/${id}/credit-ledger`, "Kredi hareketleri");
  return d.entries ?? [];
}

export async function listCustomerAnalyses(id: number): Promise<AnalysisEntry[]> {
  const d = await getJSON<{ analyses: AnalysisEntry[] | null }>(
    `/api/v1/admin/customers/${id}/analyses`, "Analizler");
  return d.analyses ?? [];
}

export async function getCustomerAnalysis(id: number, aid: number): Promise<AnalysisEntry> {
  return getJSON<AnalysisEntry>(`/api/v1/admin/customers/${id}/analyses/${aid}`, "Analiz");
}

export async function listCustomerChatSessions(id: number): Promise<ChatSessionRow[]> {
  const d = await getJSON<{ sessions: ChatSessionRow[] | null }>(
    `/api/v1/admin/customers/${id}/chat-sessions`, "Sohbetler");
  return d.sessions ?? [];
}

// --- Global AI ayarları (API key / model / bütçe — tüm müşterilerde ortak) ---

export type GlobalSettings = {
  openai_text_model: string;
  openai_api_key_set: boolean;
  openai_daily_usd: number;
  openai_monthly_usd: number;
  gemini_text_model: string;
  gemini_api_key_set: boolean;
  gemini_daily_usd: number;
  gemini_monthly_usd: number;
  deepseek_text_model: string;
  deepseek_api_key_set: boolean;
  deepseek_daily_usd: number;
  deepseek_monthly_usd: number;
  // default_ai_provider — TÜM kuruluşlar için tek sağlayıcı seçimi
  // ("deepseek" | "openai" | "gemini"). Boş ise backend DeepSeek kullanır.
  default_ai_provider: string;
  rag_provider: string;
  rag_model: string;
  gspr_provider: string;
  gspr_model: string;
  fallback_provider: string;
  fallback_model: string;
  voyage_api_key_set: boolean;
  doc_price_usd: number;
  usd_try_rate: number;
  usd_eur_rate: number;
  price_gspr_generation_credits: number;
  price_gspr_audit_credits: number;
  price_report_edit_credits: number;
  price_doc_analysis_credits: number;
  price_doc_edit_credits: number;
  price_translation_credits: number;
  doc_analysis_active: boolean;
  translation_active: boolean;
  updated_at?: string;
};

export type GlobalSettingsUpdate = {
  openai_text_model?: string;
  openai_api_key?: string; // boş → değişmez
  openai_daily_usd?: number;
  openai_monthly_usd?: number;
  gemini_text_model?: string;
  gemini_api_key?: string;
  gemini_daily_usd?: number;
  gemini_monthly_usd?: number;
  deepseek_text_model?: string;
  deepseek_api_key?: string;
  deepseek_daily_usd?: number;
  deepseek_monthly_usd?: number;
  default_ai_provider?: string;
  rag_provider?: string;
  rag_model?: string;
  gspr_provider?: string;
  gspr_model?: string;
  fallback_provider?: string;
  fallback_model?: string;
  voyage_api_key?: string;
  doc_price_usd?: number;
  usd_try_rate?: number;
  usd_eur_rate?: number;
  price_gspr_generation_credits?: number;
  price_gspr_audit_credits?: number;
  price_report_edit_credits?: number;
  price_doc_analysis_credits?: number;
  price_doc_edit_credits?: number;
  price_translation_credits?: number;
  doc_analysis_active?: boolean;
  translation_active?: boolean;
};

export async function getGlobalSettings(): Promise<GlobalSettings> {
  const res = await apiFetch("/api/v1/admin/settings");
  if (!res.ok) throw new Error(`Ayarlar alınamadı (HTTP ${res.status}).`);
  return res.json();
}

export async function updateGlobalSettings(patch: GlobalSettingsUpdate): Promise<GlobalSettings> {
  const res = await apiFetch("/api/v1/admin/settings", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await errText(res, "Ayarlar kaydedilemedi"));
  return res.json();
}

// --- Corpus (ortak mevzuat havuzu) ---

export type SourceDocument = { name: string; type: "pdf" | "csv" | "txt" | "docx"; regulation?: string };
export type SourcesStats = { files: number; chunks: number; is_indexing: boolean; index: string };
export type IngestResult = {
  files: number; indexed: number; skipped: number; chunks: number;
  errors?: string[]; index_reset: boolean; batch_size: number;
};
export type UploadResult = { name: string; path: string; size: number; extension: string; regulation?: string };

export async function getSources(): Promise<SourceDocument[]> {
  const res = await apiFetch("/api/v1/admin/sources");
  if (!res.ok) throw new Error(`Kaynaklar alınamadı (HTTP ${res.status}).`);
  const data = (await res.json()) as { documents: SourceDocument[] };
  return data.documents ?? [];
}

export async function getSourcesStats(): Promise<SourcesStats> {
  const res = await apiFetch("/api/v1/admin/sources/stats");
  if (!res.ok) throw new Error(`İstatistik alınamadı (HTTP ${res.status}).`);
  return res.json();
}

export async function uploadSource(file: File, subdir?: string): Promise<UploadResult> {
  const form = new FormData();
  form.append("file", file);
  if (subdir) form.append("subdir", subdir);
  const res = await apiFetch("/api/v1/admin/sources/upload", { method: "POST", body: form });
  if (!res.ok) throw new Error(await errText(res, "Yükleme başarısız"));
  return res.json();
}

export async function ingestSources(opts?: { reset?: boolean; only?: string[] }): Promise<IngestResult> {
  const params = new URLSearchParams();
  if (opts?.reset) params.set("reset", "true");
  if (opts?.only && opts.only.length > 0) params.set("only", opts.only.join(","));
  const qs = params.toString();
  const res = await apiFetch(`/api/v1/admin/sources/ingest${qs ? `?${qs}` : ""}`, { method: "POST" });
  if (!res.ok) throw new Error(await errText(res, "İndeksleme başarısız"));
  return res.json();
}

async function errText(res: Response, fallback: string): Promise<string> {
  try {
    const j = (await res.json()) as { error?: string };
    return j.error ? `${fallback}: ${j.error}` : `${fallback} (HTTP ${res.status}).`;
  } catch {
    return `${fallback} (HTTP ${res.status}).`;
  }
}
